import React, { useEffect, useState } from "react";
import { Award, CalendarDays, CheckCircle2, Flame, StickyNote, Target, X } from "lucide-react";

const DISMISS_KEY = "xz-weekly-dismissed";

const DOT = {
  done: "bg-green-400",
  partial: "bg-yellow-400",
  missed: "bg-red-400/70",
  outside: "bg-gray-600",
};

const Stat = ({ icon: Icon, value, label, tone }) => (
  <div className="flex items-center gap-2 rounded-xl px-3 py-2 bg-gray-700/50">
    <Icon className={`w-4 h-4 ${tone}`} />
    <div>
      <div className={`text-lg font-bold leading-none tabular-nums ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    </div>
  </div>
);

/**
 * Weekly review. `variant="card"` auto-shows on Mondays (dismissible per week,
 * per device); `variant="modal"` opens from the palette / W key any time.
 * `stats` = { days:[{label,status}], done, partial, missed, goalsDone, streak, bestStreak, notes, weekKey }.
 */
const WeeklyReview = ({ variant = "card", stats, open, onClose, onOpen }) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === (stats && stats.weekKey);
    } catch (e) {
      return false;
    }
  });
  useEffect(() => {
    if (variant !== "modal" || !open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant, open, onClose]);

  if (!stats) return null;

  const body = (
    <>
      <div className="flex items-center gap-1.5 mb-3" aria-label="Last 7 days">
        {stats.days.map((d, i) => (
          <div key={i} className="flex flex-col items-center gap-1 flex-1">
            <span className={`w-full h-2.5 rounded-full ${DOT[d.status] || DOT.outside}`} />
            <span className="text-[10px] text-gray-400">{d.label}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat icon={CheckCircle2} value={stats.done} label="Days done" tone="text-green-400" />
        <Stat icon={CalendarDays} value={stats.partial} label="Partial" tone="text-yellow-400" />
        <Stat icon={Target} value={stats.goalsDone} label="Goals done" tone="text-cyan-400" />
        <Stat icon={Flame} value={stats.streak} label="Streak" tone="text-orange-400" />
        <Stat icon={Award} value={stats.bestStreak} label="Best" tone="text-purple-400" />
        <Stat icon={StickyNote} value={stats.notes} label="Notes" tone="text-yellow-300" />
      </div>
      <p className="text-xs text-gray-400 mt-3">
        {stats.done >= 6
          ? "Outstanding week — nearly every day done."
          : stats.done >= 4
            ? "Solid week. Keep the rhythm going."
            : stats.done >= 1
              ? "Some progress — pick one small win for today."
              : "A fresh week. One day at a time."}
      </p>
    </>
  );

  if (variant === "modal") {
    if (!open) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="bg-gray-800 rounded-3xl border-2 border-cyan-500/30 shadow-2xl w-full max-w-lg p-5"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Weekly review"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              Weekly review
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="tap p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-cyan-300"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {body}
        </div>
      </div>
    );
  }

  if (dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, stats.weekKey);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <div className="xz-hide-in-focus mb-4 rounded-2xl border-2 border-cyan-500/30 bg-gray-800 card-shadow p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-base font-bold text-yellow-400 flex items-center gap-2">
          <CalendarDays className="w-4 h-4" />
          Your week in review
        </h3>
        <div className="flex items-center gap-1">
          {onOpen && (
            <button
              type="button"
              onClick={onOpen}
              className="text-xs text-cyan-300 hover:text-cyan-200 underline px-2"
            >
              Open
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="tap p-1.5 rounded-lg text-gray-400 hover:text-cyan-300 hover:bg-gray-700"
            aria-label="Dismiss for this week"
            title="Dismiss for this week"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {body}
    </div>
  );
};

export default WeeklyReview;
